import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrgId } from "@/shared/auth/orgContext";
import {
  getComplianceRun,
  listComplianceRuns,
  startComplianceRun,
  type ComplianceRun,
  type ComplianceRunDetail,
  type StartComplianceRunRequest,
} from "../api/iaComplianceApi";
import { iaKeys } from "./iaComplianceKeys";

export const RUN_POLL_INTERVAL_MS = 5000;
export const RUNS_LIST_POLL_INTERVAL_MS = 15_000;

/**
 * Pure poll-interval predicate for the active-run query, mirroring
 * templateJobPollInterval: stop on terminal statuses; keep polling ONLY
 * through transient network failures (axios error without a response). HTTP
 * errors and non-axios errors — e.g. a zod contract mismatch, which no amount
 * of retrying heals — stop the poll; the card surfaces them. The server's own
 * read-time stale reconciliation flips a stuck run to "failed" within 20
 * minutes, so polling a queued/running run always terminates.
 */
export function complianceRunPollInterval(
  data: ComplianceRunDetail | undefined,
  error: unknown,
): number | false {
  if (data?.run.status === "completed" || data?.run.status === "failed") {
    return false;
  }
  if (error) {
    if (isAxiosError(error) && !error.response) {
      return RUN_POLL_INTERVAL_MS;
    }
    return false;
  }
  return RUN_POLL_INTERVAL_MS;
}

/**
 * Pure poll-interval predicate for the run HISTORY list: refresh while any
 * run is still in flight so non-selected runs don't freeze at "Running" (only
 * the selected run has a detail poll), stop once everything is terminal.
 */
export function runsListPollInterval(
  data: ComplianceRun[] | undefined,
): number | false {
  return data?.some((r) => r.status === "queued" || r.status === "running")
    ? RUNS_LIST_POLL_INTERVAL_MS
    : false;
}

export interface UseComplianceRunsResult {
  runs: ComplianceRun[];
  runsLoading: boolean;
  runsError: boolean;
  refetchRuns: () => void;
  /** Starts a run and selects it so the detail poll begins immediately. */
  startRun: (req: StartComplianceRunRequest) => void;
  isStarting: boolean;
  startError: unknown;
  selectedRunId: string | null;
  selectRun: (runId: string | null) => void;
  /** Detail (run + per-report results) for the selected run, polled at 5s
   *  while the run is queued/running. */
  runDetail: ComplianceRunDetail | undefined;
  runDetailLoading: boolean;
  runDetailError: boolean;
  /** Refetches the selected run's detail; restarts a poll stopped by an error. */
  refetchRunDetail: () => void;
}

export interface UseComplianceRunsOptions {
  /** Fires after a run is created and selected (e.g. toast + scroll). */
  onRunStarted?: (runId: string) => void;
}

/**
 * Compliance-run state for the Internal Affairs tab: run history, the
 * start-run mutation, and a 5s poll on the selected run that stops once the
 * run reaches a terminal status (or the fetch fails with an HTTP error).
 */
export function useComplianceRuns(
  options?: UseComplianceRunsOptions,
): UseComplianceRunsResult {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: iaKeys.runs(orgId ?? ""),
    queryFn: ({ signal }) => listComplianceRuns(signal),
    enabled: !!orgId,
    staleTime: 30_000,
    refetchInterval: (query) => runsListPollInterval(query.state.data),
  });

  // Resume watching an in-flight run after a remount (tab switch resets the
  // selection): the 5s detail poll is keyed on the selection, so without this
  // a returning admin would watch the slower list poll instead.
  const runsData = runsQuery.data;
  useEffect(() => {
    if (selectedRunId !== null) return;
    const active = runsData?.find(
      (r) => r.status === "queued" || r.status === "running",
    );
    if (active) setSelectedRunId(active.runId);
  }, [runsData, selectedRunId]);

  const startMutation = useMutation({
    mutationFn: (req: StartComplianceRunRequest) => startComplianceRun(req),
    onSuccess: ({ runId }) => {
      setSelectedRunId(runId);
      void queryClient.invalidateQueries({ queryKey: iaKeys.runs(orgId ?? "") });
      options?.onRunStarted?.(runId);
    },
  });

  const runQuery = useQuery({
    queryKey: iaKeys.run(orgId ?? "", selectedRunId ?? ""),
    queryFn: ({ signal }) => getComplianceRun(selectedRunId ?? "", signal),
    enabled: !!orgId && !!selectedRunId,
    // The poll predicate owns failure handling; retrying would delay the
    // "check failed" feedback (templateJobQuery rationale).
    retry: false,
    staleTime: 0,
    refetchInterval: (query) =>
      complianceRunPollInterval(query.state.data, query.state.error),
  });

  // When the polled run transitions to a terminal status, the history list's
  // snapshot of it (status/checkedCount/violationCount) is stale — refresh it.
  // Transition-gated so merely selecting an already-finished run doesn't
  // refetch the list.
  const runStatus = runQuery.data?.run.status;
  const prevRef = useRef<{ runId: string | null; status?: string }>({
    runId: null,
  });
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { runId: selectedRunId, status: runStatus };
    if (prev.runId !== selectedRunId) return;
    if (
      (prev.status === "queued" || prev.status === "running") &&
      (runStatus === "completed" || runStatus === "failed")
    ) {
      void queryClient.invalidateQueries({ queryKey: iaKeys.runs(orgId ?? "") });
    }
  }, [selectedRunId, runStatus, queryClient, orgId]);

  return {
    runs: runsQuery.data ?? [],
    runsLoading: runsQuery.isPending,
    runsError: runsQuery.isError,
    refetchRuns: () => void runsQuery.refetch(),
    startRun: (req) => startMutation.mutate(req),
    isStarting: startMutation.isPending,
    startError: startMutation.error,
    selectedRunId,
    selectRun: setSelectedRunId,
    runDetail: runQuery.data,
    runDetailLoading: !!selectedRunId && runQuery.isPending,
    runDetailError: runQuery.isError,
    refetchRunDetail: () => void runQuery.refetch(),
  };
}
